// Voice Overlay - VENTURA: Small. Mighty. Fast. Precise.
// Pit-wall engineer aesthetic. Mechanical confidence. Never cute.

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import type { VoiceState, VoiceIntent } from '@/types';

interface VoiceOverlayProps {
  state: VoiceState;
  transcript: string;
  intent?: VoiceIntent;
  resultCount?: number;
  topMatchConfidence?: number;
  errorType?: 'no-speech' | 'low-confidence' | 'timeout' | 'offline' | 'permission-denied' | 'microphone-unavailable';
  suggestions?: string[];
  onCancel?: () => void;
  onConfirm?: () => void;
  onRetry?: () => void;
}

export function VoiceOverlay({
  state,
  transcript,
  intent,
  resultCount,
  topMatchConfidence,
  errorType,
  suggestions,
  onCancel,
  onConfirm,
  onRetry,
}: VoiceOverlayProps) {
  const [showResult, setShowResult] = useState(false);

  // Auto-dismiss result confirmation
  useEffect(() => {
    if (resultCount !== undefined && state === 'idle') {
      setShowResult(true);
      const timer = setTimeout(() => setShowResult(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [resultCount, state]);

  const isListening = state === 'listening-command';
  const isProcessing = state === 'processing';
  const isError = state === 'error';
  // Only show full overlay for processing and error - listening is now subtle
  const isFullOverlayVisible = isProcessing || isError;

  return (
    <>
      {/* Listening indicator - subtle edge glow, doesn't block content */}
      <AnimatePresence>
        {isListening && (
          <ListeningEdgeGlow onCancel={onCancel} transcript={transcript} />
        )}
      </AnimatePresence>

      {/* Full overlay - only for processing/error states */}
      {/* z-[60] ensures visibility over showcase mode (z-50) */}
      <AnimatePresence>
        {isFullOverlayVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="fixed inset-0 z-[60]"
          >
            {/* Scrim - tap to cancel */}
            <div
              className="absolute inset-0 bg-workshop-bg/85"
              onClick={onCancel}
            />

            {/* Hard cancel - top right */}
            <button
              onClick={onCancel}
              className="absolute top-6 right-6 z-10 w-14 h-14 flex items-center justify-center rounded-workshop bg-workshop-surface text-workshop-text-muted hover:text-white transition-colors"
              aria-label="Stop"
            >
              <CloseIcon className="w-6 h-6" />
            </button>

            {/* Content - lower third */}
            <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pb-12 px-6">
              {isProcessing && (
                <ProcessingModule
                  transcript={transcript}
                  intent={intent}
                  suggestions={suggestions}
                />
              )}

              {isError && (
                <ErrorModule
                  errorType={errorType || 'no-speech'}
                  heardText={transcript}
                  onRetry={onRetry!}
                  onCancel={onCancel!}
                  onSearchAnyway={onConfirm}
                  suggestions={suggestions}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result confirmation - brief flash */}
      <AnimatePresence>
        {showResult && resultCount !== undefined && (
          <ResultFlash
            count={resultCount}
            confidence={topMatchConfidence}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * Listening Edge Glow - Colorful pulsing glow around screen edges
 * Content remains fully visible - only glow effects, no solid borders
 * Dynamic waveform that reacts to speech
 * z-[60] ensures visibility over showcase mode (z-50)
 */
function ListeningEdgeGlow({ onCancel, transcript }: { onCancel?: () => void; transcript?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[60] pointer-events-none"
    >
      {/* Subtle pulsing glow at edges - reduced 50% */}
      <motion.div
        className="absolute inset-0"
        animate={{
          boxShadow: [
            'inset 0 0 60px 10px rgba(139, 92, 246, 0.25), inset 0 0 120px 20px rgba(59, 130, 246, 0.12)',
            'inset 0 0 80px 15px rgba(236, 72, 153, 0.28), inset 0 0 150px 25px rgba(139, 92, 246, 0.15)',
            'inset 0 0 60px 10px rgba(59, 130, 246, 0.25), inset 0 0 120px 20px rgba(236, 72, 153, 0.12)',
            'inset 0 0 80px 15px rgba(139, 92, 246, 0.28), inset 0 0 150px 25px rgba(59, 130, 246, 0.15)',
            'inset 0 0 60px 10px rgba(139, 92, 246, 0.25), inset 0 0 120px 20px rgba(59, 130, 246, 0.12)',
          ],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Corner accent glows - reduced 50% */}
      <motion.div
        className="absolute top-0 left-0 w-56 h-56"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.05, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(circle at top left, rgba(139, 92, 246, 0.3) 0%, transparent 65%)' }}
      />
      <motion.div
        className="absolute top-0 right-0 w-56 h-56"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.05, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.45 }}
        style={{ background: 'radial-gradient(circle at top right, rgba(236, 72, 153, 0.3) 0%, transparent 65%)' }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-56 h-56"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.05, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.9 }}
        style={{ background: 'radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.3) 0%, transparent 65%)' }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-56 h-56"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.05, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 1.35 }}
        style={{ background: 'radial-gradient(circle at bottom right, rgba(34, 197, 94, 0.3) 0%, transparent 65%)' }}
      />

      {/* Top status bar with dynamic waveform */}
      <div className="absolute top-0 inset-x-0 flex justify-center">
        <motion.div
          initial={{ y: -80 }}
          animate={{ y: 0 }}
          exit={{ y: -80 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          className="pointer-events-auto flex items-center gap-4 px-6 py-3 mt-4 bg-workshop-bg/95 backdrop-blur-xl border border-workshop-accent/30 rounded-xl"
          style={{
            boxShadow: '0 0 30px rgba(139, 92, 246, 0.3), 0 0 60px rgba(59, 130, 246, 0.2)',
          }}
        >
          <DynamicWaveform transcript={transcript} />

          <span className="text-workshop-accent text-sm font-mono uppercase tracking-wider">
            VENTURA LISTENING
          </span>

          <button
            onClick={onCancel}
            className="ml-2 px-3 py-1 text-xs font-mono uppercase tracking-wider text-workshop-text-muted hover:text-white bg-workshop-surface hover:bg-workshop-border rounded transition-colors"
          >
            STOP
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}

/**
 * Dynamic Waveform - reacts to transcript changes (speech detection)
 */
function DynamicWaveform({ transcript }: { transcript?: string }) {
  const NUM_BARS = 16;
  // Initialize with varied heights so animation is visible immediately
  const [heights, setHeights] = useState<number[]>(() =>
    Array.from({ length: NUM_BARS }, (_, i) => {
      const centerBias = 1 - Math.abs(i - NUM_BARS / 2) / (NUM_BARS / 2) * 0.4;
      return 0.1 + centerBias * 0.1 + Math.random() * 0.05;
    })
  );
  const lastTranscriptRef = useRef(transcript);
  const lastTranscriptLengthRef = useRef(transcript?.length || 0);
  const isSpeakingRef = useRef(false);
  const speakingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Detect when transcript changes (user is speaking)
  useEffect(() => {
    const currentLength = transcript?.length || 0;
    const lastLength = lastTranscriptLengthRef.current;

    // Detect speech by either content change or length increase
    if (transcript !== lastTranscriptRef.current || currentLength > lastLength) {
      lastTranscriptRef.current = transcript;
      lastTranscriptLengthRef.current = currentLength;
      isSpeakingRef.current = true;

      // Reset speaking state after 400ms of no changes
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current);
      }
      speakingTimeoutRef.current = setTimeout(() => {
        isSpeakingRef.current = false;
      }, 400);
    }
  }, [transcript]);

  // Animate bars continuously
  useEffect(() => {
    const interval = setInterval(() => {
      setHeights(prev => {
        return prev.map((h, i) => {
          // Center bars are taller
          const centerBias = 1 - Math.abs(i - NUM_BARS / 2) / (NUM_BARS / 2) * 0.4;

          let target: number;
          if (isSpeakingRef.current) {
            // Active speaking - dramatic, high bars with variation
            const variation = 0.5 + Math.random() * 0.5;
            target = Math.min(1, centerBias * variation * 1.3);
          } else {
            // Idle - subtle breathing animation
            const wave = Math.sin(Date.now() / 400 + i * 0.3) * 0.08;
            target = 0.12 + wave + Math.random() * 0.06;
          }

          // Smooth interpolation: fast rise, graceful fall
          const speed = target > h ? 0.4 : 0.1;
          return h + (target - h) * speed;
        });
      });
    }, 40);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-0.5 h-8">
      {heights.map((height, i) => (
        <div
          key={i}
          className="w-1 rounded-full bg-workshop-accent"
          style={{
            height: `${3 + height * 25}px`,
            opacity: 0.5 + height * 0.5,
            transition: 'height 80ms ease-out, opacity 80ms ease-out',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Listening Module - Waveform + transcript + controls
 */
function ListeningModule({
  transcript,
  onCancel,
  onDone,
}: {
  transcript: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-2xl"
    >
      {/* VENTURA header */}
      <div className="text-center mb-4">
        <span className="text-workshop-accent text-label font-mono uppercase tracking-widest">
          VENTURA
        </span>
      </div>

      {/* Status */}
      <p className="text-center text-workshop-text-muted text-sm uppercase tracking-wider mb-6">
        LISTENING
      </p>

      {/* Waveform */}
      <div className="flex justify-center mb-8">
        <Waveform />
      </div>

      {/* Transcript */}
      <div className="min-h-[100px] flex items-center justify-center mb-8">
        <p className="text-headline text-white text-center max-w-xl">
          {transcript || (
            <span className="text-workshop-text-muted">Go ahead</span>
          )}
        </p>
      </div>

      {/* Controls - huge buttons */}
      <div className="flex justify-center gap-4">
        <button
          onClick={onCancel}
          className={clsx(
            'min-w-[140px] h-touch-lg px-8 rounded-workshop',
            'bg-workshop-surface text-white text-workshop font-medium',
            'border border-workshop-border',
            'hover:bg-workshop-border active:scale-[0.98] transition-all'
          )}
        >
          STOP
        </button>
        <button
          onClick={onDone}
          className={clsx(
            'min-w-[140px] h-touch-lg px-8 rounded-workshop',
            'bg-workshop-accent text-white text-workshop font-medium',
            'hover:bg-workshop-accent-hover active:scale-[0.98] transition-all'
          )}
        >
          DONE
        </button>
      </div>
    </motion.div>
  );
}

/**
 * Processing Module - "THINKING"
 */
function ProcessingModule({
  transcript,
  intent,
  suggestions,
}: {
  transcript: string;
  intent?: VoiceIntent;
  suggestions?: string[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-xl"
    >
      {/* Header */}
      <p className="text-center text-workshop-warning text-label font-mono uppercase tracking-widest mb-6">
        THINKING
      </p>

      {/* What was heard */}
      <div className="text-center mb-6">
        <span className="text-workshop-text-muted text-xs uppercase tracking-wider">
          You said
        </span>
        <p className="text-workshop text-white mt-1">"{transcript}"</p>
      </div>

      {/* Progress sweep */}
      <div className="mb-6">
        <div className="h-1 bg-workshop-surface rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-workshop-warning"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 2, ease: 'linear' }}
          />
        </div>
      </div>

      {/* Intent echo */}
      {intent && intent.type !== 'unknown' && (
        <div className="bg-workshop-surface rounded-workshop p-4">
          <p className="text-workshop-text-muted text-xs uppercase tracking-wider mb-3">
            UNDERSTOOD AS
          </p>
          <div className="flex flex-wrap gap-2">
            {intent.component && (
              <IntentChip label="Component" value={intent.component} confidence={intent.confidence} />
            )}
            {intent.condition && (
              <IntentChip label="Condition" value={intent.condition.replace('-', ' ')} confidence={intent.confidence} />
            )}
            {intent.phase && (
              <IntentChip label="Phase" value={intent.phase.replace('-', ' ')} confidence={intent.confidence} />
            )}
            {intent.type === 'compare' && (
              <IntentChip label="Action" value="Compare" confidence={intent.confidence} />
            )}
            {intent.type === 'similar' && (
              <IntentChip label="Action" value="Similar" confidence={intent.confidence} />
            )}
          </div>
          {/* Confidence */}
          <div className="flex justify-end mt-3">
            <span className={clsx(
              'text-xs font-mono uppercase',
              intent.confidence >= 0.8 && 'text-workshop-success',
              intent.confidence >= 0.5 && intent.confidence < 0.8 && 'text-workshop-warning',
              intent.confidence < 0.5 && 'text-workshop-error'
            )}>
              {intent.confidence >= 0.8 ? 'HIGH' : intent.confidence >= 0.5 ? 'OK' : 'LOW'}
            </span>
          </div>
          {/* Suggestions for low confidence */}
          {intent.confidence < 0.5 && suggestions && suggestions.length > 0 && (
            <div className="mt-4 p-3 bg-workshop-bg rounded-lg">
              <p className="text-workshop-text-muted text-xs uppercase tracking-wider mb-2">
                TRY SAYING
              </p>
              <div className="space-y-1">
                {suggestions.map((suggestion, i) => (
                  <p key={i} className="text-workshop-accent text-sm">
                    {suggestion}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Intent Chip with confidence tick
 */
function IntentChip({
  label,
  value,
  confidence,
}: {
  label: string;
  value: string;
  confidence: number;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-workshop-bg rounded-lg">
      <span className="text-workshop-text-muted text-xs uppercase">{label}</span>
      <span className="text-white text-sm capitalize">{value}</span>
      <ConfidenceTick confidence={confidence} />
    </div>
  );
}

/**
 * Confidence tick mark
 */
function ConfidenceTick({ confidence }: { confidence: number }) {
  const style = confidence >= 0.8 ? 'solid' : confidence >= 0.5 ? 'hollow' : 'dashed';
  const color = confidence >= 0.8 ? '#22c55e' : confidence >= 0.5 ? '#f59e0b' : '#a3a3a3';

  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16">
      <path
        d="M3 8l3 3 7-7"
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={style === 'dashed' ? '3 2' : style === 'hollow' ? '1 2' : 'none'}
      />
    </svg>
  );
}

/**
 * Result Flash - "DONE • N IMAGES"
 */
function ResultFlash({
  count,
  confidence,
}: {
  count: number;
  confidence?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
    >
      <div className="flex items-center gap-4 px-6 py-3 bg-workshop-success/20 border border-workshop-success/30 rounded-workshop">
        <span className="text-workshop-success font-mono uppercase tracking-wider">
          DONE
        </span>
        <span className="text-white font-medium">
          {count} IMAGES
        </span>
        {confidence !== undefined && (
          <>
            <span className="text-workshop-text-muted">•</span>
            <span className="text-workshop-text-muted font-mono text-sm">
              BEST {confidence}%
            </span>
          </>
        )}
      </div>
    </motion.div>
  );
}

/**
 * Error Module - Blunt, not apologetic
 */
function ErrorModule({
  errorType,
  heardText,
  onRetry,
  onCancel,
  onSearchAnyway,
  suggestions,
}: {
  errorType: 'no-speech' | 'low-confidence' | 'timeout' | 'offline' | 'permission-denied' | 'microphone-unavailable';
  heardText?: string;
  onRetry: () => void;
  onCancel: () => void;
  onSearchAnyway?: () => void;
  suggestions?: string[];
}) {
  const content = {
    'no-speech': { title: 'NOTHING HEARD', body: 'Too much noise', action: 'Try again in a quieter area' },
    'low-confidence': { title: 'NOT SURE', body: 'Request unclear', action: 'Speak clearly and try again' },
    'timeout': { title: 'TOO SLOW', body: 'Search exceeded limit', action: 'Try a simpler query' },
    'offline': { title: 'OFFLINE', body: 'No network connection', action: 'Check your internet connection' },
    'permission-denied': { title: 'NO ACCESS', body: 'Microphone blocked', action: 'Enable microphone in browser settings' },
    'microphone-unavailable': { title: 'NO MIC', body: 'Microphone not found', action: 'Connect a microphone' },
  }[errorType];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md"
    >
      <div className="bg-workshop-surface rounded-workshop p-8 border border-workshop-border">
        {/* Title */}
        <h3 className="text-center text-headline text-workshop-warning font-mono uppercase tracking-wider mb-2">
          {content.title}
        </h3>

        {/* Body */}
        <p className="text-center text-workshop-text-muted mb-2">
          {content.body}
        </p>

        {/* Recovery hint */}
        <p className="text-center text-workshop-text-muted/70 text-xs mt-2">
          {content.action}
        </p>

        {/* What was heard */}
        {heardText && errorType === 'low-confidence' && (
          <p className="text-center text-workshop-text-muted/50 text-sm mb-4">
            Heard: "{heardText}"
          </p>
        )}

        {/* Suggestions for low confidence */}
        {errorType === 'low-confidence' && suggestions && suggestions.length > 0 && (
          <div className="mt-4">
            <p className="text-workshop-text-muted text-xs uppercase tracking-wider mb-2 text-center">
              TRY SAYING
            </p>
            <div className="space-y-1 text-center">
              {suggestions.map((suggestion, i) => (
                <p key={i} className="text-workshop-accent text-sm">
                  {suggestion}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Telemetry for timeout */}
        {errorType === 'timeout' && (
          <p className="text-center text-workshop-text-muted font-mono text-xs mb-4">
            {'>'} 3.0s
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-6 py-3 rounded-workshop bg-workshop-border text-white hover:bg-workshop-surface transition-all"
          >
            STOP
          </button>
          <button
            onClick={onRetry}
            className="px-6 py-3 rounded-workshop bg-workshop-accent text-white hover:bg-workshop-accent-hover transition-all"
          >
            RETRY
          </button>
          {errorType === 'low-confidence' && onSearchAnyway && (
            <button
              onClick={onSearchAnyway}
              className="px-6 py-3 rounded-workshop bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white transition-all"
            >
              GO
            </button>
          )}
          {errorType === 'timeout' && (
            <button
              onClick={onCancel}
              className="px-6 py-3 rounded-workshop bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white transition-all"
            >
              FILTER
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Waveform - input-level driven bars
 */
function Waveform() {
  const bars = 20;

  return (
    <div className="flex items-center justify-center gap-1 h-16 w-64">
      {Array.from({ length: bars }).map((_, i) => (
        <motion.div
          key={i}
          className="w-1.5 rounded-full bg-workshop-accent"
          animate={{
            height: [12, 24 + Math.random() * 40, 12],
          }}
          transition={{
            duration: 0.3 + Math.random() * 0.2,
            repeat: Infinity,
            repeatType: 'reverse',
            delay: i * 0.02,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Voice Status Indicator for header
 */
export function VoiceStatusIndicator({
  state,
  voiceMode = 'full',
  isLoading = false,
  loadProgress = '',
  isOffline = false,
  className,
}: {
  state: VoiceState;
  voiceMode?: 'ptt' | 'full';
  isLoading?: boolean;
  loadProgress?: string;
  isOffline?: boolean;
  className?: string;
}) {
  const isArmed = state === 'listening-wake';
  const isActive = state === 'listening-command';
  const isProcessing = state === 'processing';
  const isIdle = state === 'idle';
  const isPttIdle = voiceMode === 'ptt' && isIdle;

  // Loading state takes precedence
  if (isLoading) {
    const isReady = loadProgress === 'READY!';
    return (
      <div
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-all',
          isReady ? 'bg-workshop-success/20 text-workshop-success' : 'bg-workshop-warning/20 text-workshop-warning',
          className
        )}
      >
        <div className={clsx(
          'w-2 h-2 rounded-full',
          isReady ? 'bg-workshop-success' : 'bg-workshop-warning animate-pulse'
        )} />
        <span className="truncate max-w-[150px]">
          {loadProgress || 'LOADING...'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-all',
        isActive && 'bg-workshop-accent/20 text-workshop-accent',
        isArmed && 'bg-workshop-surface text-workshop-text-muted',
        isProcessing && 'bg-workshop-warning/20 text-workshop-warning',
        isPttIdle && 'bg-workshop-surface text-workshop-text-muted',
        className
      )}
    >
      <div
        className={clsx(
          'w-2 h-2 rounded-full',
          isActive && 'bg-workshop-accent animate-pulse',
          isArmed && 'bg-workshop-text-muted animate-pulse',
          isProcessing && 'bg-workshop-warning animate-pulse',
          isPttIdle && 'bg-workshop-text-muted'
        )}
      />
      <span>
        {isActive && 'LISTENING'}
        {isArmed && (isOffline ? 'OFFLINE READY' : 'READY')}
        {isProcessing && 'THINKING'}
        {isPttIdle && (isOffline ? 'OFFLINE PTT' : 'PTT')}
      </span>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
